import React, { useState, useMemo, useEffect } from 'react';
import { useShippingData } from './hooks/useShippingData';
import { useNavigate } from 'react-router-dom';
import './ShippingTable.css';

export default function ShippingTable() {
  const { shipments, loading, error, refresh, updateFilters } = useShippingData();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    country: '',
    status: '',
    carrier: '',
    warehouse: '',
  });

  const [summary, setSummary] = useState({
    total: 0,
    delivered: 0,
    inTransit: 0,
    delayed: 0,
    failed: 0,
  });

  const [pages, setPages] = useState({});

  // 🕒 Convert "Xd Yh" → total hours
  const parseDurationToHours = (str) => {
    if (!str) return 0;
    const d = str.match(/(\d+)d/);
    const h = str.match(/(\d+)h/);
    const days = d ? parseInt(d[1]) : 0;
    const hours = h ? parseInt(h[1]) : 0;
    return days * 24 + hours;
  };

  // ⚙️ Calculate average
  const calcAverage = (rows, field) => {
    const valid = rows
      .map((r) => parseDurationToHours(r[field]))
      .filter((v) => v > 0);
    if (valid.length === 0) return 0;
    return (valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(1);
  };

  // ⏱ Format hours → days/hours
  const formatHoursToDays = (hours) => {
    const total = parseFloat(hours);
    const days = Math.floor(total / 24);
    const h = Math.round(total % 24);
    if (isNaN(total)) return '—';
    return days > 0 ? `${days}d ${h}h` : `${h}h`;
  };

  // 📊 Summary calculation
  useEffect(() => {
    const total = shipments.length;
    const delivered = shipments.filter((r) => r.LATEST_EVENT_STATUS === 'delivered').length;
    const inTransit = shipments.filter((r) => r.LATEST_EVENT_STATUS === 'in_transit').length;
    const delayed = shipments.filter((r) => r.LATEST_EVENT_STATUS === 'delayed').length;
    const failed = shipments.filter((r) => r.LATEST_EVENT_STATUS === 'failure').length;
    setSummary({ total, delivered, inTransit, delayed, failed });
  }, [shipments]);

  // 🚚 Filter only delivered for averages
  const deliveredShipments = useMemo(
    () => shipments.filter((r) => r.LATEST_EVENT_STATUS === 'delivered'),
    [shipments]
  );

  const avgOrderToShip = calcAverage(deliveredShipments, 'TIME_ORDER_TO_SHIP');
  const avgShipToDeliver = calcAverage(deliveredShipments, 'TIME_SHIP_TO_DELIVER');
  const avgTotal = (parseFloat(avgOrderToShip) + parseFloat(avgShipToDeliver)).toFixed(1);

  // 🔍 Filters
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const applyFilters = () => updateFilters(filters);

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      country: '',
      status: '',
      carrier: '',
      warehouse: '',
    });
    updateFilters({});
  };

  // 🔢 Dropdown options
  const countries = [...new Set(shipments.map((s) => s.DESTINATION_COUNTRY).filter(Boolean))];
  const carriers = [...new Set(shipments.map((s) => s.TRACKING_COMPANY).filter(Boolean))];
  const statuses = [...new Set(shipments.map((s) => s.LATEST_EVENT_STATUS).filter(Boolean))];
  const warehouses = [...new Set(shipments.map((s) => s.WAREHOUSE_NAME).filter(Boolean))];

  // 🌀 Loading / Error states
  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading Shipping Data...</p>
        </div>
      </div>
    );
  }

  if (error) return <div className="error">Error: {error}</div>;

  // 📦 Group shipments by status
  const grouped = shipments.reduce((acc, row) => {
    const key = row.LATEST_EVENT_STATUS || 'unknown';
    acc[key] = acc[key] || [];
    acc[key].push(row);
    return acc;
  }, {});

  return (
    <div className="shipping-page">
      {/* ======= Header ======= */}
    <div className="shipping-header">
      <h2>📦 Shipping Dashboard</h2>
      <div className="header-buttons">
        <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
          🏠 Dashboard
        </button>
        <button className="btn-secondary" onClick={() => navigate('/factory-shipments')}>
          🏭 Factory Shipments
        </button>
        <button className="refresh-btn" onClick={refresh}>
          🔄 Refresh
        </button>
      </div>
    </div>

      {/* ======= Filters ======= */}
      <div className="filters">
        <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
        <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />

        <select name="country" value={filters.country} onChange={handleFilterChange}>
          <option value="">All Countries</option>
          {countries.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        <select name="status" value={filters.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          {statuses.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>

        <select name="carrier" value={filters.carrier} onChange={handleFilterChange}>
          <option value="">All Carriers</option>
          {carriers.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        <select name="warehouse" value={filters.warehouse} onChange={handleFilterChange}>
          <option value="">All Warehouses</option>
          {warehouses.map((w) => (
            <option key={w}>{w}</option>
          ))}
        </select>

        <div className="filter-buttons">
          <button className="apply-btn" onClick={applyFilters}>
            ✅ Apply Filters
          </button>
          <button className="reset-btn" onClick={resetFilters}>
            ♻️ Reset
          </button>
        </div>
      </div>

      {/* ======= Summary Cards ======= */}
      <div className="summary-cards">
        <div className="card total"><h4>Total Shipments</h4><p>{summary.total}</p></div>
        <div className="card delivered"><h4>Delivered</h4><p>{summary.delivered}</p></div>
        <div className="card transit"><h4>In Transit</h4><p>{summary.inTransit}</p></div>
        <div className="card delayed"><h4>Delayed</h4><p>{summary.delayed}</p></div>
        <div className="card failed"><h4>Failed</h4><p>{summary.failed}</p></div>
      </div>

      <div className="summary-cards">
        <div className="card kpi"><h4>Avg Order → Ship</h4><p>{formatHoursToDays(avgOrderToShip)}</p></div>
        <div className="card kpi"><h4>Avg Ship → Deliver</h4><p>{formatHoursToDays(avgShipToDeliver)}</p></div>
        <div className="card kpi"><h4>Total Delivery</h4><p>{formatHoursToDays(avgTotal)}</p></div>
      </div>

      {/* ======= Tables by Status ======= */}
      {Object.keys(grouped).map((status) => {
        const itemsPerPage = 20;
        const page = pages[status] || 1;
        const rows = grouped[status];
        const totalPages = Math.ceil(rows.length / itemsPerPage);
        const start = (page - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        const visibleRows = rows.slice(start, end);

        const handlePrev = () => {
          setPages((prev) => ({
            ...prev,
            [status]: Math.max(1, (prev[status] || 1) - 1),
          }));
        };

        const handleNext = () => {
          setPages((prev) => ({
            ...prev,
            [status]: Math.min(totalPages, (prev[status] || 1) + 1),
          }));
        };

        return (
          <div key={status} className="status-section">
            <div className={`status-header status-${status}`}>
              <span>{status.toUpperCase()}</span>
              <span>{rows.length}</span>
            </div>

            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Country</th>
                    <th>Warehouse</th>
                    <th>Carrier</th>
                    <th>Tracking</th>
                    <th>Status</th>
                    <th>Order → Ship</th>
                    <th>Ship → Deliver</th>
                    <th>Last Update</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.ORDER_ID}>
                      <td>{row.ORDER_NAME}</td>
                      <td>{row.DESTINATION_COUNTRY}</td>
                      <td>
                        {row.WAREHOUSE_NAME
                          ? `${row.WAREHOUSE_NAME}${row.WAREHOUSE_CITY ? ` (${row.WAREHOUSE_CITY})` : ''}`
                          : '—'}
                      </td>
                      <td>{row.TRACKING_COMPANY}</td>
                      <td>{row.TRACKING_NUMBER}</td>
                      <td>{row.CURRENT_STATUS}</td>
                      <td>{row.TIME_ORDER_TO_SHIP || '—'}</td>
                      <td>{row.TIME_SHIP_TO_DELIVER || '—'}</td>
                      <td>
                        {row.LAST_STATUS_TIME
                          ? new Date(row.LAST_STATUS_TIME).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {rows.length > itemsPerPage && (
              <div className="pagination">
                <button onClick={handlePrev} disabled={page === 1}>
                  ⬅️ Prev
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button onClick={handleNext} disabled={page === totalPages}>
                  Next ➡️
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
